import { FC, useCallback, useEffect, useState } from "react"
import HighCharts from "highcharts/highstock";
import HighchartsReact from 'highcharts-react-official'
import { addDays, eachDayOfInterval, format, parse, subDays } from "date-fns";

import { COLOR_LIST } from "src/constants/cases";
import { LINE_CHART } from "src/constants/highCharts";
import { LineChartProps } from "./types";
import { isMobile } from "src/helpers";



const CustomLineChart: FC<LineChartProps> = ({ LineChartObject, title }): JSX.Element => {
  const [lineChart, setLineChart] = useState(LINE_CHART);


  const populateLineBase = () => {
    let allDates: any[] = [];
    LineChartObject?.forEach((caseVolume: any) => {
      allDates = allDates.concat(Object.keys(caseVolume.data));
    });
    if (allDates.length === 0) return { data: [], step: 1, }

    const sortedDates = Array
      .from(new Set(allDates))
      .map((item) => parse(item, "yyyy-MM-dd", new Date()))
      .sort((a: Date, b: Date) => (a < b) ? -1 : 1)

    let start_date = sortedDates[0];
    const start_date_day = start_date.getDate()
    if (start_date_day % 2 !== 0)
      start_date = subDays(start_date, 1)

    let end_date = sortedDates[sortedDates.length - 1];
    const end_date_day = end_date.getDate()
    if (end_date_day % 2 !== 0)
      end_date = addDays(end_date, 1)

    const allDateArr = eachDayOfInterval({ start: start_date, end: end_date })

    return {
      data: allDateArr,
      step: isMobile() ? (allDateArr.length > 300 ? 1 : (allDateArr.length > 90 ? 30 : 7)) : (allDateArr.length > 550 ? 90 : (allDateArr.length < 55 ? (allDateArr.length < 16 ? 1 : 2) : 7))
    }
  }


  // Map data to the line series, filling missing dates with null
  const wrapperFunctionForLine = () => {
    const lineSeriesArray: any[] = [];
    const xAxisDates = populateLineBase().data;

    LineChartObject
      .filter((caseVolume) => ["Received", "Completed", "Same-Day Completed"].includes(caseVolume.name))
      .forEach((caseVolume, index) => {
        const seriesData: (number | null)[] = [];

        // Map over the xAxisDates, inserting data or null if missing
        xAxisDates.forEach((date) => {
          if (caseVolume.data) {
            const formattedDate = format(date, "yyyy-MM-dd");
            if (caseVolume.data[formattedDate] !== undefined) {
              if (caseVolume.data[formattedDate] === 0)
                seriesData.push(null);
              else
                seriesData.push(caseVolume.data[formattedDate]);
            }
            else
              seriesData.push(null); // Insert null for missing dates
          }
        });

        lineSeriesArray.push({
          type: "spline",
          name: ["Received", "Completed"].includes(caseVolume.name) ? `Total ${caseVolume.name}` : caseVolume.name,
          data: seriesData,
          color:COLOR_LIST[index],
          boostThreshold: 0,
        });
      });

    return lineSeriesArray;
  };



  // Populate line chart options with x-axis and series data
  const populateLineChart = useCallback(() => {
    const lineOptions = { ...lineChart };
    lineOptions.series = wrapperFunctionForLine();
    const { data, step } = populateLineBase()

    lineOptions.xAxis = {
      categories: data.map((item) => format(item, "d. MMM")),
      labels: {
        step,
        rotation: isMobile() ? -45 : 0,
      },
    };
    setLineChart(lineOptions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LineChartObject]);

  useEffect(() => {
    populateLineChart();
  }, [populateLineChart]);



  return (
    <div className="card flex-fill">
      <div className="card-header ">
        <h4 className="text-center m-0">{title}</h4>
      </div>
      <div className="card-body">
        <p className="text-center">Line convergence is positive, and higher numbers are better.</p>
        <HighchartsReact highcharts={HighCharts} options={lineChart} />
      </div>
    </div>
  )
}
export default CustomLineChart;
